import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Check } from "lucide-react";
import { MaskLine, FadeUp } from "../components/Reveal";
import { LogoLockup } from "../components/Logo";
import { SERVICES } from "../constants/services";
import { VINEOPS_URL } from "../constants/site";

export default function ServicePage() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);

  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
    if (!service) return;
    const prev = document.title;
    document.title = `${service.title} in Napa & Sonoma | Vinedresser Vineyard Management`;
    const desc = document.querySelector('meta[name="description"]');
    const prevDesc = desc?.getAttribute("content");
    desc?.setAttribute("content", `${service.summary} Serving Napa Valley and Sonoma County estates.`);
    return () => {
      document.title = prev;
      if (desc && prevDesc) desc.setAttribute("content", prevDesc);
    };
  }, [service]);

  if (!service) return <Navigate to="/" replace />;

  return (
    <div data-testid="service-page">
      <header className="border-b border-[#E5DEC9] bg-[#F9F6F0]">
        <div className="max-w-[1440px] mx-auto flex items-center justify-between px-6 md:px-12 h-[72px]">
          <Link to="/" data-testid="service-logo-link">
            <LogoLockup size="sm" />
          </Link>
          <div className="flex items-center gap-6">
            <Link
              to="/"
              data-testid="service-back-link"
              className="group inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase font-semibold text-[#364132] hover:text-[#B89B67] transition-colors duration-300"
            >
              <ArrowLeft size={13} className="transition-transform duration-300 group-hover:-translate-x-0.5" />
              Back to site
            </Link>
            <a
              data-testid="service-client-login"
              href={VINEOPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center gap-2.5 text-[11px] tracking-[0.2em] uppercase font-semibold px-5 py-3 border border-[#1A3626] text-[#1A3626] transition-all duration-300 hover:bg-[#B89B67] hover:border-[#B89B67] hover:text-[#12271B]"
            >
              <img src="/assets/vineops-mark.png" alt="VineOps" className="h-[11px] w-auto" />
              Client Login
            </a>
          </div>
        </div>
      </header>

      <section className="py-24 md:py-36">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <FadeUp>
            <p className="text-[11px] tracking-[0.32em] uppercase text-[#B89B67] font-semibold mb-10">Services</p>
          </FadeUp>
          <h1 className="font-display font-light text-[#1A3626] text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight max-w-[16ch]">
            <MaskLine delay={0.1}>{service.title}</MaskLine>
          </h1>
          <FadeUp delay={0.35} className="mt-10 max-w-[54ch]">
            <p className="text-base md:text-lg leading-relaxed text-[#2C2926]/80">{service.intro}</p>
          </FadeUp>
        </div>
      </section>

      <section data-testid="service-included-section" className="pb-24 md:pb-36">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <FadeUp>
            <h2 className="font-display font-light text-3xl md:text-4xl text-[#1A3626] mb-12">How We Can Help</h2>
          </FadeUp>
          <div>
            {service.included.map((item, i) => (
              <FadeUp key={item} delay={i * 0.06}>
                <div data-testid={`service-included-${i + 1}`} className="hairline-t py-7 flex items-start gap-5 group">
                  <Check size={17} className="text-[#B89B67] shrink-0 mt-1" strokeWidth={2} />
                  <p className="font-display font-light text-xl md:text-2xl text-[#2C2926]/85 transition-transform duration-500 group-hover:translate-x-2">{item}</p>
                </div>
              </FadeUp>
            ))}
            <div className="hairline-t" />
          </div>

          <FadeUp delay={0.2} className="mt-16">
            <Link
              to="/#contact"
              data-testid="service-cta-button"
              className="group inline-flex items-center gap-3 bg-[#1A3626] text-[#F9F6F0] text-[11px] tracking-[0.22em] uppercase font-semibold px-10 py-4 transition-colors duration-300 hover:bg-[#B89B67] hover:text-[#12271B] active:scale-95"
            >
              Start a conversation
              <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
